/**
 * Formula result card — actionable card for generated formulas.
 *
 * Flow: Generate → FormulaCard → click "Inserer" → preview panel → confirm → insert.
 * Actions: Insert into Excel (with preview), Copy to clipboard, Explain (navigate).
 * Displays warnings and AI explanation.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Text, MessageBar, MessageBarBody, Badge } from '@fluentui/react-components';
import {
  ArrowDownloadRegular,
  CopyRegular,
  LightbulbRegular,
  MathFormulaRegular,
} from '@fluentui/react-icons';
import { ResultCard, type ResultCardAction } from '../common/ResultCard';
import { ActionPreviewPanel, type PreviewImpact } from '../common/ActionPreviewPanel';
import { CodeBlock } from '../common/CodeBlock';
import { insertFormula, readActiveFormula } from '../../office';
import type { FormulaParseMode } from '@excel-ai-studio/shared';

function getParseModeBadge(mode: FormulaParseMode): { label: string; color: 'success' | 'warning' | 'danger' } {
  if (mode === 'strict-json') return { label: 'Fiabilite elevée', color: 'success' };
  if (mode === 'extracted-json') return { label: 'Fiabilite moyenne', color: 'warning' };
  return { label: 'Verification recommandee', color: 'danger' };
}

interface FormulaCardProps {
  formula: string;
  explanation?: string;
  warnings?: readonly string[];
  parseMode?: FormulaParseMode;
  confidenceScore?: number;
  alternativeSuggestion?: string;
  /** When true, insert via cell.formulasLocal (for localized function names). */
  isLocal?: boolean;
  /** Callback to navigate to the Explain page with this formula pre-filled. */
  onExplain?: (formula: string) => void;
}

export const FormulaCard: React.FC<FormulaCardProps> = ({
  formula,
  explanation,
  warnings,
  parseMode,
  confidenceScore,
  alternativeSuggestion,
  isLocal,
  onExplain,
}) => {
  // Preview state: null = no preview, object = showing confirmation panel
  const [preview, setPreview] = useState<PreviewImpact | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [insertError, setInsertError] = useState<string>();

  // Read the active cell info and open the preview panel
  const handleInsertClick = useCallback(async () => {
    setInsertError(undefined);
    setPreviewLoading(true);
    try {
      const info = await readActiveFormula();
      const currentStr =
        info.formula ??
        (info.cellValue != null && info.cellValue !== '' ? String(info.cellValue) : undefined);

      setPreview({
        target: info.cellAddress,
        description: 'La formule sera inseree dans la cellule active.',
        cellsAffected: 1,
        currentValue: currentStr ?? '',
        newValue: formula,
      });
    } catch {
      // If we can't read the cell (e.g. not in Excel), still show preview without current value
      setPreview({
        target: 'cellule active',
        description: 'La formule sera inseree dans la cellule active.',
        cellsAffected: 1,
        newValue: formula,
      });
    } finally {
      setPreviewLoading(false);
    }
  }, [formula]);

  // Confirm: perform the actual insertion
  const handleConfirm = useCallback(async () => {
    setInsertError(undefined);
    await insertFormula(formula, isLocal ? { local: true } : undefined);
    // Auto-dismiss preview after short delay
    setTimeout(() => setPreview(null), 1500);
  }, [formula, isLocal]);

  const handleCancel = useCallback(() => {
    setPreview(null);
    setInsertError(undefined);
  }, []);

  const actions = useMemo<ResultCardAction[]>(() => {
    const list: ResultCardAction[] = [
      {
        key: 'insert',
        label: previewLoading ? 'Lecture...' : 'Inserer dans Excel',
        icon: <ArrowDownloadRegular />,
        primary: true,
        tooltip: 'Previsualiser avant insertion dans la cellule active',
        disabled: preview !== null || previewLoading,
        onClick: handleInsertClick,
      },
      {
        key: 'copy',
        label: 'Copier',
        icon: <CopyRegular />,
        feedbackLabel: 'Copie !',
        onClick: () => navigator.clipboard.writeText(formula),
      },
    ];

    if (onExplain) {
      list.push({
        key: 'explain',
        label: 'Expliquer',
        icon: <LightbulbRegular />,
        onClick: () => onExplain(formula),
      });
    }

    return list;
  }, [formula, isLocal, onExplain, preview, previewLoading, handleInsertClick]);

  return (
    <ResultCard
      title="Formule proposee"
      icon={<MathFormulaRegular />}
      variant="formula"
      actions={actions}
    >
      <CodeBlock code={formula} label="Formule" />

      {parseMode && (
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text size={200} style={{ color: 'var(--text-secondary)' }}>
            Mode de parsing: <strong>{parseMode}</strong>
          </Text>
          <Badge size="small" color={getParseModeBadge(parseMode).color}>
            {getParseModeBadge(parseMode).label}
          </Badge>
        </div>
      )}
      {typeof confidenceScore === 'number' && (
        <Text size={200} style={{ color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>
          Confiance estimee: <strong>{confidenceScore}%</strong>
        </Text>
      )}

      {insertError && (
        <Text size={200} style={{ color: 'var(--error)', marginTop: 4, display: 'block' }}>
          {insertError}
        </Text>
      )}

      {warnings && warnings.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {warnings.map((w, i) => (
            <MessageBar key={i} intent="warning" style={{ borderRadius: 4 }}>
              <MessageBarBody>{w}</MessageBarBody>
            </MessageBar>
          ))}
        </div>
      )}

      {explanation && (
        <div style={{ marginTop: 12 }}>
          <Text size={200} weight="semibold" style={{ color: 'var(--text-secondary)' }}>
            Explication
          </Text>
          <Text size={300} style={{ display: 'block', marginTop: 4, whiteSpace: 'pre-wrap' }}>
            {explanation}
          </Text>
        </div>
      )}

      {alternativeSuggestion && (
        <MessageBar intent="info" style={{ borderRadius: 4, marginTop: 10 }}>
          <MessageBarBody>
            Alternative proposee: {alternativeSuggestion}
          </MessageBarBody>
        </MessageBar>
      )}

      {/* ── Preview / Confirmation panel ── */}
      {preview && (
        <ActionPreviewPanel
          impact={preview}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          confirmLabel="Confirmer l'insertion"
          confirmIcon={<ArrowDownloadRegular />}
        />
      )}
    </ResultCard>
  );
};
